import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useDataProvider, useGetList } from 'react-admin';
import type { ImpersonationSession, Market, User } from '../dataProvider/types';
import { expiresAfter, sessionPhase } from './impersonationState';

/**
 * Holds the active view-only impersonation session, app-wide (D-impersonation). Wraps the
 * Layout exactly as RegionProvider does, so the persistent indicator and the member's-eye
 * view share one source of truth across every route.
 *
 * The active session is DERIVED off the clock from the recorded sessions (the single source
 * of truth is the data, not separate in-memory state), so it survives a reload and clears
 * by EXPIRY with no write. START creates a session (audited), EXPLICIT END updates endedAt
 * (audited); EXPIRY is derived (now past expiresAt), never written. The actor is recorded by
 * the audit floor, not on the row, so the mock treats any active session as the current
 * one (a single-admin simplification; the real backend scopes sessions to the admin).
 */

export interface ActiveSession {
  id: number;
  subjectUserId: number;
  subjectName: string;
  market: Market;
  startedAt: string;
  expiresAt: string;
}

export interface ImpersonationContextValue {
  activeSession: ActiveSession | null;
  isLoading: boolean;
  start: (subject: {
    id: number;
    name: string;
    market: Market;
  }) => Promise<void>;
  end: () => Promise<void>;
}

const ImpersonationContext = createContext<
  ImpersonationContextValue | undefined
>(undefined);

const LIST = {
  pagination: { page: 1, perPage: 1000 },
  sort: { field: 'id', order: 'DESC' as const },
};

/** Re-render cadence so a live session clears at expiry without a reload (production only). */
const TICK_MS = 30_000;

export const ImpersonationProvider = ({
  children,
  now: injectedNow,
}: {
  children: ReactNode;
  /** Injected clock. Tests pass a fixed clock; production omits it (Date.now plus a tick). */
  now?: () => number;
}) => {
  const now = useMemo(() => injectedNow ?? (() => Date.now()), [injectedNow]);
  const injected = injectedNow !== undefined;
  const dataProvider = useDataProvider();
  const {
    data: sessions,
    isLoading,
    refetch,
  } = useGetList<ImpersonationSession>('impersonationSessions', LIST);
  const { data: users } = useGetList<User>('users', LIST);

  // A tick forces re-derivation so the indicator clears at expiry in a live session. Only
  // runs when no clock was injected (production); tests drive the clock directly.
  const [, setTick] = useState(0);
  useEffect(() => {
    // The default clock means production; a passed clock means a test drives it.
    if (injected) return;
    const handle = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nameById = useMemo(
    () => new Map((users ?? []).map((u) => [u.id, u.name])),
    [users],
  );

  const activeSession = useMemo<ActiveSession | null>(() => {
    const nowMs = now();
    const active = (sessions ?? [])
      .filter((s) => sessionPhase(s, nowMs) === 'active')
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
    if (!active) return null;
    return {
      id: active.id,
      subjectUserId: active.subjectUserId,
      subjectName: nameById.get(active.subjectUserId) ?? '',
      market: active.market,
      startedAt: active.startedAt,
      expiresAt: active.expiresAt,
    };
    // `now` is read each render; sessions / names drive the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, nameById]);

  const value = useMemo<ImpersonationContextValue>(
    () => ({
      activeSession,
      isLoading,
      start: async (subject) => {
        const startedMs = now();
        await dataProvider.create('impersonationSessions', {
          data: {
            subjectUserId: subject.id,
            market: subject.market,
            startedAt: new Date(startedMs).toISOString(),
            expiresAt: expiresAfter(startedMs),
            endedAt: null,
          },
        });
        await refetch();
      },
      end: async () => {
        if (!activeSession) return;
        await dataProvider.update('impersonationSessions', {
          id: activeSession.id,
          data: { endedAt: new Date(now()).toISOString() },
          previousData: { id: activeSession.id, endedAt: null },
        });
        await refetch();
      },
    }),
    [activeSession, isLoading, dataProvider, refetch, now],
  );

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};

/** Access the impersonation context. Must be used within an ImpersonationProvider. */
export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) {
    throw new Error(
      'useImpersonation must be used within an ImpersonationProvider',
    );
  }
  return ctx;
}
